import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const DEFAULT_LABELS = {
  dashboard: "Dashboard",
  musings: "Musings",
  content: "Content Studio",
  prompts: "Prompts",
  team: "Team",
  customers: "Customers",
  schedule: "Schedule",
  profile: "Company Profile",
};

const ORG_LABELS = {
  team: "Staff",
  customers: "Members",
  profile: "Organization Profile",
};

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const { user } = useAuth();
  const [orgType, setOrgType] = useState("company");
  const [labels, setLabels] = useState(DEFAULT_LABELS);

  useEffect(() => {
    if (!user) { setOrgType("company"); setLabels(DEFAULT_LABELS); return; }
    const ot = user.org_type || "company";
    setOrgType(ot);
    const base = { ...DEFAULT_LABELS, ...(ot === "organization" ? ORG_LABELS : {}) };
    const overrides = user.section_labels || {};
    setLabels({ ...base, ...overrides });
  }, [user]);

  const label = useCallback((k) => labels[k] || DEFAULT_LABELS[k] || k, [labels]);

  const renameLabel = async (key, value) => {
    const next = { ...labels, [key]: value };
    setLabels(next);
    try {
      await api.put("/user/settings", { section_labels: next });
    } catch {}
  };

  const setOrg = async (next) => {
    setOrgType(next);
    const base = { ...DEFAULT_LABELS, ...(next === "organization" ? ORG_LABELS : {}) };
    // Only adopt the new defaults for keys the user hasn't customized
    const merged = { ...base };
    for (const k of Object.keys(labels)) {
      // keep their custom values
      if (labels[k] && labels[k] !== DEFAULT_LABELS[k] && labels[k] !== ORG_LABELS[k]) {
        merged[k] = labels[k];
      }
    }
    setLabels(merged);
    try {
      await api.put("/user/settings", { org_type: next, section_labels: merged });
    } catch {}
  };

  return (
    <SettingsContext.Provider value={{ orgType, setOrg, labels, label, renameLabel }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext) || {
  orgType: "company", labels: DEFAULT_LABELS, label: (k)=>DEFAULT_LABELS[k], renameLabel: ()=>{}, setOrg: ()=>{},
};
